import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Upload,
  FileBarChart2,
  Users,
  LogOut,
  ShieldCheck,
  Sparkles,
  Settings,
  UserCircle,
  Swords,
  Archive,
  ExternalLink,
  Brain,
  Image as ImageIcon,
  FileSpreadsheet,
  Camera,
  Share2,
  TrendingUp,
  Plug,
  CloudDownload,
  LineChart,
  Link2,
  Lock,
  Warehouse,
  CreditCard,
} from "lucide-react";
import { BrandLogo } from "./BrandLogo";
import { getUser, logout, api } from "../lib/api";

const RECHARGE_WA = (email) =>
  `https://wa.me/919464645491?text=${encodeURIComponent(
    `Hi! I want to recharge my EcomCatalog AI account (${email || "my account"}) to continue listing.`
  )}`;

const SELLER_NAV = [
  { to: "/seller/auto-listing", label: "Auto Listings", icon: FileSpreadsheet, id: "nav-auto-listing", tier_key: "auto_listing" },
  { to: "/seller/upload", label: "Upload Data", icon: Upload, id: "nav-upload", tier_key: "upload" },
  { to: "/seller", label: "Dashboard", icon: LayoutDashboard, id: "nav-dashboard", tier_key: "dashboard" },
  { to: "/seller/analytics", label: "Growth Analytics", icon: LineChart, id: "nav-analytics", tier_key: "analytics" },
  { to: "/seller/insights", label: "Ads Suggestions", icon: Sparkles, id: "nav-insights", tier_key: "insights" },
  { to: "/seller/growth", label: "Conversion %", icon: Brain, id: "nav-growth", tier_key: "growth" },
  { to: "/seller/reports", label: "Reports", icon: FileBarChart2, id: "nav-reports", tier_key: "reports" },
  { to: "/seller/myntra", label: "Myntra Hub", icon: Link2, id: "nav-myntra-hub", tier_key: "myntra_hub" },
  { to: "/seller/catalogue", label: "My Catalogue", icon: Archive, id: "nav-catalogue", tier_key: "catalogue" },
  { to: "/seller/billing", label: "Billing", icon: CreditCard, id: "nav-billing", tier_key: "billing" },
  { to: "/seller/profile", label: "Profile", icon: UserCircle, id: "nav-profile", tier_key: "profile" },
];

// Plan tier → allowed sidebar keys. Keep in sync with `TIER_NAV_ACCESS` in
// /app/backend/server.py. Items whose `tier_key` is NOT in the seller's
// `tier_features` array (from /auth/me) get hidden from the sidebar.
const TIER_NAV_ACCESS = {
  starter:  new Set(["auto_listing", "profile", "myntra_hub", "billing"]),
  pro:      new Set(["auto_listing", "upload", "dashboard", "analytics", "insights", "growth", "reports", "myntra_hub", "profile", "billing"]),
  business: new Set(["auto_listing", "upload", "dashboard", "analytics", "insights", "growth", "reports", "myntra_hub", "catalogue", "profile", "billing"]),
};

const ADMIN_NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, id: "nav-admin-overview" },
  { to: "/admin/sellers", label: "Sellers", icon: Users, id: "nav-admin-sellers" },
  { to: "/admin/marketplace", label: "Marketplace", icon: Warehouse, id: "nav-admin-marketplace" },
  { to: "/admin/templates", label: "Templates", icon: FileSpreadsheet, id: "nav-admin-templates" },
  { to: "/admin/brands", label: "BrandsByIndia", icon: Sparkles, id: "nav-admin-brands" },
  { to: "/admin/payments", label: "Payments", icon: CreditCard, id: "nav-admin-payments" },
  { to: "/admin/settings", label: "Settings", icon: Settings, id: "nav-admin-settings" },
];

export function AppShell({ children, role = "seller" }) {
  const [user, setUser] = React.useState(getUser());
  const location = useLocation();
  const baseNav = role === "admin" ? ADMIN_NAV : SELLER_NAV;
  // Resolve the seller's plan tier. Locked tabs still RENDER in the sidebar
  // (per product decision) but are visually demoted with a lock icon and
  // route the seller to /pricing instead of opening the gated page.
  const planTier = (user?.plan_tier && TIER_NAV_ACCESS[user.plan_tier]) ? user.plan_tier : "business";
  // Trial accounts: ONLY the Listings tab is usable — every data-analysis
  // tab renders locked until they buy the ₹999/month unlock (100 listings
  // + Data Analysis).
  const TRIAL_NAV_ACCESS = new Set(["auto_listing", "profile", "billing"]);
  const isTrialUser = role === "seller" && user?.account_type === "trial";
  const allowedKeys = role === "admin" ? null : (isTrialUser ? TRIAL_NAV_ACCESS : TIER_NAV_ACCESS[planTier]);
  // No filtering — every item is shown. Annotate with `locked` so the
  // renderer can swap the Link target + add lock affordance.
  const nav = baseNav.map((item) => ({
    ...item,
    locked: role === "seller" && allowedKeys && item.tier_key && !allowedKeys.has(item.tier_key),
  }));

  // Refresh the cached user on every navigation so trial counters and
  // subscription state stay current (e.g. right after an Excel generate).
  React.useEffect(() => {
    let on = true;
    api
      .get("/auth/me")
      .then(({ data }) => {
        if (!on) return;
        localStorage.setItem("bb_user", JSON.stringify(data));
        setUser(data);
      })
      .catch(() => {});
    return () => {
      on = false;
    };
  }, [location.pathname]);

  const expired = user?.subscription_status && user.subscription_status !== "active";
  const isTrial = role === "seller" && user?.account_type === "trial";
  const trialUsed = user?.trial_listings_used || 0;
  const trialLimit = user?.trial_listings_limit || 2;
  const trialExhausted = isTrial && trialUsed >= trialLimit;
  const daysLeft = (() => {
    if (!user?.subscription_expires_at) return null;
    const ms = new Date(user.subscription_expires_at).getTime() - Date.now();
    return Math.ceil(ms / (1000 * 60 * 60 * 24));
  })();

  return (
    <div className="app-light-theme min-h-screen flex relative" style={{ background: "#FAFAFB", color: "#282C3F" }}>
      {/* Soft Myntra-tinted ambient backdrop */}
      <div
        className="fixed inset-0 pointer-events-none z-0"
        style={{
          background:
            "radial-gradient(900px 600px at 0% 0%, rgba(255,63,108,0.06), transparent 60%), radial-gradient(800px 600px at 100% 100%, rgba(255,144,90,0.05), transparent 60%)",
        }}
      />

      {/* Sidebar — white card */}
      <aside
        className="w-64 shrink-0 flex flex-col p-4 gap-2 sticky top-0 h-screen z-10"
        style={{
          background: "#FFFFFF",
          borderRight: "1px solid #EDEDF1",
          boxShadow: "0 0 32px -8px rgba(15, 23, 42, 0.04)",
        }}
        data-testid="app-sidebar"
      >
        <div className="flex items-center justify-between mb-5 mt-1 px-1">
          <BrandLogo size={36} showWordmark />
        </div>

        <div
          className="px-3 text-[10px] uppercase tracking-[0.25em] font-semibold mb-1"
          style={{ color: "#94969F" }}
        >
          {role === "admin" ? "Control Room" : "Seller Workspace"}
        </div>
        <nav className="flex flex-col gap-0.5">
          {nav.map((item) => {
            const Icon = item.icon;
            const active = location.pathname === item.to;
            const locked = !!item.locked;
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={item.id}
                title={locked ? `Upgrade your plan to unlock ${item.label}` : item.label}
                data-locked={locked || undefined}
                className="group relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all"
                style={
                  locked
                    ? {
                        color: "#94969F",
                        border: "1px solid transparent",
                        opacity: 0.78,
                        cursor: "pointer",
                      }
                    : active
                    ? {
                        background:
                          "linear-gradient(90deg, rgba(255,63,108,0.10), rgba(255,144,90,0.05) 60%, transparent)",
                        color: "#FF3F6C",
                        border: "1px solid rgba(255,63,108,0.22)",
                        boxShadow: "0 6px 18px -10px rgba(255,63,108,0.35)",
                      }
                    : {
                        color: "#1A1C28",
                        border: "1px solid transparent",
                      }
                }
                onMouseEnter={(e) => {
                  if (locked) {
                    e.currentTarget.style.background = "rgba(255,63,108,0.05)";
                    e.currentTarget.style.color = "#FF3F6C";
                    return;
                  }
                  if (!active) {
                    e.currentTarget.style.background = "#F8F8FA";
                    e.currentTarget.style.color = "#0B0C12";
                  }
                }}
                onMouseLeave={(e) => {
                  if (locked) {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.color = "#94969F";
                    return;
                  }
                  if (!active) {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.color = "#1A1C28";
                  }
                }}
              >
                <Icon
                  size={16}
                  style={{ color: locked ? "#B2B3BD" : active ? "#FF3F6C" : "#3A3D4D" }}
                />
                <span className={locked ? "line-through decoration-[#E2E2EB] flex-1" : "flex-1"}>{item.label}</span>
                {locked && (
                  <span
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-bold"
                    style={{ background: "#FFF1F5", color: "#FF3F6C", border: "1px solid #FFD3DD" }}
                    data-testid={`${item.id}-lock`}
                  >
                    <Lock size={9} /> Upgrade
                  </span>
                )}
                {active && !locked && (
                  <span
                    className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 rounded-r-full"
                    style={{
                      background:
                        "linear-gradient(to bottom, #FF3F6C, #FF905A)",
                    }}
                  />
                )}
              </Link>
            );
          })}
        </nav>

        <div className="mt-auto">
          <div
            className="p-3.5 rounded-2xl mb-3"
            style={{
              background: "#F8F8FA",
              border: "1px solid #EDEDF1",
            }}
          >
            <div
              className="text-[10px] uppercase tracking-widest font-semibold"
              style={{ color: "#94969F" }}
            >
              Signed in as
            </div>
            <div className="flex items-center gap-2.5 mt-1">
              {user?.picture ? (
                <img
                  src={user.picture}
                  alt=""
                  referrerPolicy="no-referrer"
                  className="w-9 h-9 rounded-full object-cover shrink-0"
                  style={{ border: "2px solid #FFD3DD" }}
                  data-testid="sidebar-avatar"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              ) : (
                <span
                  className="w-9 h-9 rounded-full grid place-items-center text-xs font-extrabold shrink-0"
                  style={{ background: "#FFF1F5", color: "#FF3F6C", border: "2px solid #FFD3DD" }}
                  data-testid="sidebar-avatar-initials"
                >
                  {(user?.name || "U").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                </span>
              )}
              <div className="min-w-0">
                <div
                  className="text-sm font-semibold truncate"
                  style={{ color: "#282C3F" }}
                  data-testid="sidebar-user-name"
                >
                  {user?.name || "User"}
                </div>
                <div className="text-xs truncate" style={{ color: "#7E818C" }}>
                  {user?.email}
                </div>
              </div>
            </div>
            {role === "seller" && daysLeft !== null && (
              <div className="mt-2 text-[11px]" style={{ color: "#535665" }}>
                <span className="inline-flex items-center gap-1.5">
                  <ShieldCheck size={11} style={{ color: "#FF3F6C" }} />
                  {daysLeft > 0 ? `${daysLeft}d subscription left` : "Expired"}
                </span>
              </div>
            )}
          </div>
          {role === "seller" && (
            <button
              onClick={async () => {
                const marketingUrl =
                  process.env.REACT_APP_MARKETING_URL || "https://ecomcatalog.ai";
                try {
                  const { api } = await import("../lib/api");
                  const { data } = await api.get(
                    `/auth/sso/token?return_to=${encodeURIComponent(marketingUrl)}`
                  );
                  window.open(data.redirect_url, "_blank");
                } catch (e) {
                  window.open(marketingUrl, "_blank");
                }
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors"
              style={{ color: "#535665" }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#F8F8FA";
                e.currentTarget.style.color = "#FF3F6C";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = "#535665";
              }}
              data-testid="open-marketing-btn"
            >
              <ExternalLink size={14} /> Open ecomcatalog.ai
            </button>
          )}
          <button
            onClick={() => logout()}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors"
            style={{ color: "#535665" }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#F8F8FA";
              e.currentTarget.style.color = "#FF3F6C";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = "#535665";
            }}
            data-testid="logout-btn"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col relative z-10">
        {isTrial && !trialExhausted && (
          <div
            className="text-white text-sm px-6 py-2 flex items-center justify-between gap-3"
            style={{
              background: "linear-gradient(90deg, #4F46E5 0%, #7C3AED 100%)",
            }}
            data-testid="trial-banner"
          >
            <span>
              <b>Free Trial</b> — {trialUsed} of {trialLimit} free listings used.
              Recharge anytime to unlock unlimited listings.
            </span>
            <a
              href={RECHARGE_WA(user?.email)}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 px-3 py-1 rounded-full bg-white/15 hover:bg-white/25 font-semibold text-xs transition-colors"
              data-testid="trial-banner-recharge-btn"
            >
              Recharge on WhatsApp
            </a>
          </div>
        )}
        {expired && role === "seller" && (
          <div
            className="text-white text-sm px-6 py-2 flex items-center justify-between"
            style={{
              background:
                "linear-gradient(90deg, #FF3F6C 0%, #FF905A 100%)",
            }}
            data-testid="subscription-banner"
          >
            <span>
              Your subscription is {user?.subscription_status}. Please contact your admin to renew.
            </span>
          </div>
        )}
        <div className="flex-1 p-8">{children}</div>

        {/* Trial exhausted — full-screen recharge wall */}
        {trialExhausted && (
          <div
            className="fixed inset-0 z-50 grid place-items-center p-6"
            style={{ background: "rgba(13,15,24,0.72)", backdropFilter: "blur(8px)" }}
            data-testid="trial-exhausted-overlay"
          >
            <div
              className="max-w-md w-full rounded-3xl p-8 text-center"
              style={{
                background: "#FFFFFF",
                border: "1px solid #EDEDF1",
                boxShadow: "0 30px 80px -20px rgba(255,63,108,0.45)",
              }}
            >
              <div
                className="mx-auto w-14 h-14 rounded-2xl grid place-items-center mb-4"
                style={{
                  background: "linear-gradient(135deg, rgba(255,63,108,0.12), rgba(255,144,90,0.12))",
                  border: "1px solid rgba(255,63,108,0.25)",
                }}
              >
                <Sparkles size={24} style={{ color: "#FF3F6C" }} />
              </div>
              <h2 className="text-2xl font-bold" style={{ color: "#282C3F" }}>
                Free trial complete 🎉
              </h2>
              <p className="text-sm mt-2 leading-relaxed" style={{ color: "#535665" }}>
                You&apos;ve used your free AI listing. Recharge now to unlock
                more listings, reports and every other
                EcomCatalog AI feature.
              </p>
              <a
                href={RECHARGE_WA(user?.email)}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl text-white font-bold text-sm transition-transform hover:-translate-y-0.5"
                style={{
                  background: "#25D366",
                  boxShadow: "0 14px 30px -10px rgba(37,211,102,0.55)",
                }}
                data-testid="trial-recharge-whatsapp-btn"
              >
                Recharge on WhatsApp — +91 94646 45491
              </a>
              <a
                href="mailto:info@ecomcatalog.ai?subject=Recharge my EcomCatalog AI account"
                className="mt-3 block text-xs font-medium"
                style={{ color: "#7E818C" }}
                data-testid="trial-recharge-email-link"
              >
                or email info@ecomcatalog.ai
              </a>
              <button
                onClick={() => logout()}
                className="mt-5 text-xs font-semibold"
                style={{ color: "#94969F" }}
                data-testid="trial-overlay-logout"
              >
                Sign out
              </button>
            </div>
          </div>
        )}
        <footer
          className="px-8 py-6 text-xs flex items-center justify-between"
          style={{ borderTop: "1px solid #EDEDF1", color: "#94969F" }}
        >
          <span data-testid="footer-attribution">Made by EcomCatalog AI — All Rights Reserved.</span>
          <span className="font-mono-data">v2.0</span>
        </footer>
      </main>
    </div>
  );
}

export default AppShell;
